import { Box, Divider, Flex, HStack, Skeleton, Text } from 'design-system';
import { useGetSalesLookupFamilyApi } from 'apis/sales-lookup';
import { usePageParams } from 'hooks/use-page-params';
import { AttributeChips } from 'pages/sales-lookup/components/attribute-chips';
import { HeadlinePricePanel } from 'pages/sales-lookup/components/headline-price';
import { MobileSelect } from 'pages/sales-lookup/components/mobile-select';
import { VariantMenuOption, VariantValueOption } from 'pages/sales-lookup/components/variant-option';
import { MobileShell } from 'pages/sales-lookup/components/mobile-shell';
import { SupplierPriceList } from 'pages/sales-lookup/components/supplier-price-list';
import { useBasket } from 'pages/sales-lookup/hooks/use-basket';
import { buildVariantPricing, latestClosedForRestaurant } from 'pages/sales-lookup/utils/headline';
import { formatPrice, freshness } from 'pages/prices/utils/matrix-display';
import { resolveVatMode, VatMode, withVat } from 'pages/sales-lookup/utils/vat';
import { tierUnit } from 'utils/price-units';
import {
  applyFacet,
  defaultVariant,
  facetDefs,
  facetOptions,
  FacetSelection,
  filterVariants,
} from 'pages/sales-lookup/utils/variants';
import qs from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const FamilyDetailScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const familyId = Number(familyIdParam) || undefined;

  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const vatMode = resolveVatMode(params.vat);
  const restaurantId = Number(params.restaurantId) || undefined;

  useEffect(() => {
    dispatch(changeTitle('Sales Lookup'));
  }, []);

  const { data, isLoading } = useGetSalesLookupFamilyApi(familyId, restaurantId);
  const detail = data?.data;

  // Narrowing state: one selection map across all facets. `undefined` values
  // mean "All"; explicit null is a facet's "none" chip (No brand / Unknown).
  const [selection, setSelection] = useState<FacetSelection>({});
  const [pickedVariantId, setPickedVariantId] = useState<number | null>(null);
  const [initializedFamilyId, setInitializedFamilyId] = useState<number | null>(null);

  // Default to the variant with the most recent closed price once data lands,
  // re-defaulting whenever a param-only navigation lands a different family in
  // this same mounted instance. Only brand + origin are pre-narrowed — the
  // remaining facets start at "All" so sibling variants stay in the picker.
  useEffect(() => {
    if (!detail || initializedFamilyId === detail.familyId) return;
    const { variantId } = getPageParams();
    const fromParam = detail.variants.find((v) => v.id === Number(variantId) && v.isActive);
    const dv = fromParam ?? defaultVariant(detail.variants, detail.closed);
    setSelection(dv ? { brand: dv.brand, origin: dv.origin } : {});
    setPickedVariantId(dv?.id ?? null);
    setInitializedFamilyId(detail.familyId);
  }, [detail, initializedFamilyId]);

  const facets = useMemo(() => (detail ? facetDefs(detail.variants) : []), [detail]);

  const matches = useMemo(() => (detail ? filterVariants(detail.variants, selection) : []), [detail, selection]);

  const selectedVariant = matches.find((v) => v.id === pickedVariantId) ?? matches[0] ?? null;

  const pricing = useMemo(
    () => (detail && selectedVariant ? buildVariantPricing(detail, selectedVariant.id) : null),
    [detail, selectedVariant]
  );

  const overlay = useMemo(
    () =>
      detail && selectedVariant && restaurantId
        ? latestClosedForRestaurant(detail, selectedVariant.id, restaurantId)
        : null,
    [detail, selectedVariant, restaurantId]
  );
  const overlaySupplierName = overlay
    ? detail?.suppliers.find((s) => s.supplierId === overlay.supplierId)?.supplierName ?? null
    : null;
  const restaurantName = (params.restaurantName as string) || '';

  // One basket line per variant: a variant already in the basket re-opens its
  // form for editing instead of adding a duplicate.
  const basket = useBasket();
  const basketItem = selectedVariant ? basket.findByVariant(selectedVariant.id) : undefined;

  const selectFacet = (key: string, value: string | null | undefined) => {
    if (!detail) return;
    setSelection(applyFacet(detail.variants, selection, key, value));
    setPickedVariantId(null);
  };

  const setVatMode = (mode: VatMode) => {
    setPageParams({ ...getPageParams(), vat: mode });
  };

  const goBack = () => {
    const { restaurantId: rid, restaurantName, vat, search } = getPageParams();
    history.push({
      pathname: '/app/sales-lookup/list',
      search: qs.stringify({ restaurantId: rid, restaurantName, vat, search }, { skipEmptyString: true }),
    });
  };

  const goToFeedback = () => {
    if (!selectedVariant) return;
    const { restaurantId: rid, restaurantName, vat, search } = getPageParams();
    history.push({
      pathname: `/app/sales-lookup/families/${familyId}/feedback`,
      search: qs.stringify(
        { restaurantId: rid, restaurantName, vat, search, variantId: selectedVariant.id, basketItem: basketItem?.key },
        { skipEmptyString: true }
      ),
    });
  };

  return (
    <MobileShell>
      <HStack as='button' spacing='4px' mb='12px' onClick={goBack}>
        <FiChevronLeft />
        <Text fontSize='14px' fontWeight='semibold' color='gray.700'>
          All products
        </Text>
      </HStack>

      {isLoading ? (
        <Skeleton h='320px' borderRadius='16px' />
      ) : !detail ? (
        <Text fontSize='14px' color='gray.500' textAlign='center' py='40px'>
          Product not found
        </Text>
      ) : (
        <Box bg='white' borderRadius='16px' border='1px solid' borderColor='gray.100' p='16px'>
          <Flex align='start' justify='space-between' mb='14px'>
            <Text fontSize='20px' fontWeight='bold' color='gray.900'>
              {detail.universalName}
            </Text>
            {detail.categoryName && (
              <Box bg='red.50' px='10px' py='3px' borderRadius='full' flexShrink={0} ml='8px'>
                <Text fontSize='10px' fontWeight='bold' letterSpacing='0.08em' color='red.600'>
                  {detail.categoryName.toUpperCase()}
                </Text>
              </Box>
            )}
          </Flex>

          {facets.map((facet) => (
            <AttributeChips
              key={facet.key}
              label={facet.label}
              options={facetOptions(detail.variants, facet.key)}
              selected={selection[facet.key]}
              onSelect={(value) => selectFacet(facet.key, value)}
              nullLabel={facet.nullLabel}
            />
          ))}

          {matches.length > 1 && (
            <Box mb='12px'>
              <MobileSelect
                label='VARIANT'
                options={matches}
                optionKey={(v) => String(v.id)}
                isSelected={(v) => v.id === selectedVariant?.id}
                onSelect={(v) => setPickedVariantId(v.id)}
                renderOption={(v) => <VariantMenuOption variant={v} />}
                renderValue={() =>
                  selectedVariant ? (
                    <VariantValueOption variant={selectedVariant} />
                  ) : (
                    <Text fontSize='13.5px' fontWeight='semibold' color='gray.500'>
                      Select a variant
                    </Text>
                  )
                }
              />
            </Box>
          )}

          <Divider my='14px' />

          {selectedVariant && pricing ? (
            <>
              {overlay && (
                <Box bg='red.50' border='1px solid' borderColor='red.100' borderRadius='10px' px='12px' py='8px' mb='12px'>
                  <Text fontSize='12.5px' color='gray.800'>
                    <Text as='span' fontWeight='bold'>
                      {restaurantName || 'This customer'}
                    </Text>{' '}
                    last closed AED {formatPrice(withVat(overlay.price, vatMode))}
                    {tierUnit(selectedVariant, overlay.priceUnit) ? ` / ${tierUnit(selectedVariant, overlay.priceUnit)}` : ''} via{' '}
                    {overlaySupplierName ?? 'Unknown supplier'} ({freshness(overlay.closedAt)?.label ?? ''})
                  </Text>
                </Box>
              )}

              <HeadlinePricePanel
                headline={pricing.headline}
                ladder={selectedVariant}
                vatMode={vatMode}
                onVatModeChange={setVatMode}
              />

              <SupplierPriceList
                pricing={pricing}
                ladder={selectedVariant}
                vatMode={vatMode}
                highlightRestaurantId={restaurantId}
              />

              <Box
                as='button'
                w='full'
                mt='16px'
                py='12px'
                borderRadius='12px'
                bg='red.600'
                _hover={{ bg: 'red.700' }}
                onClick={goToFeedback}>
                <Text fontSize='15px' fontWeight='bold' color='white'>
                  {basketItem ? 'In basket · Edit' : 'Add to basket'}
                </Text>
              </Box>
            </>
          ) : (
            <Text fontSize='14px' color='gray.500' py='20px' textAlign='center'>
              No active variants
            </Text>
          )}
        </Box>
      )}
    </MobileShell>
  );
};
